import type { ReactNode } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ems/ui/components/select'

export type Option = {
  value: string
  label: string
  /** A small mark before the label — a status dot. Shown in the trigger too, once chosen. */
  leading?: ReactNode
}

/**
 * A single choice from a short list — roles, editions — on the design system's `Select`.
 *
 * The props a `Field` hands its control (`id`, `aria-describedby`, `aria-invalid`) go on the
 * **trigger**, because that is the element that takes focus: a `<label htmlFor>` pointing anywhere
 * else names nothing a screen reader lands on.
 *
 * The list drops below the trigger (`popper`). Radix's default, `item-aligned`, lays the list over
 * the trigger with the chosen item on top of it, and a list taller than the screen then grows and
 * shifts as it scrolls — sixty ticket types do exactly that.
 *
 * `value=""` shows the placeholder. Radix reserves the empty string for "nothing chosen", which is
 * why no option may use it — a "none" choice needs a real value of its own.
 */
export function OptionSelect({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  size = 'default',
  className,
  id,
  required,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: {
  value: string
  onValueChange: (value: string) => void
  options: readonly Option[]
  placeholder?: string
  disabled?: boolean
  size?: 'sm' | 'default'
  className?: string
  id?: string
  required?: boolean
  'aria-label'?: string
  'aria-describedby'?: string | undefined
  'aria-invalid'?: boolean
}) {
  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      {...(disabled === undefined ? {} : { disabled })}
      {...(required === undefined ? {} : { required })}
    >
      <SelectTrigger
        size={size}
        className={className ?? 'w-full min-w-40'}
        {...(id === undefined ? {} : { id })}
        {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
        {...(ariaDescribedBy === undefined ? {} : { 'aria-describedby': ariaDescribedBy })}
        {...(ariaInvalid === undefined ? {} : { 'aria-invalid': ariaInvalid })}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent position="popper">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.leading === undefined ? (
              option.label
            ) : (
              <span className="flex items-center gap-2">
                {option.leading}
                {option.label}
              </span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
